<template>

  <v-container class="pa-0 ma-0 step delivery-step-obtain-receiver-info" v-if="route && order && receiverLocation">

    <!------------------->
    <!-- Location info -->
    <!------------------->
    <v-row class="pa-0 ma-0 mt-1">
      <v-col sm="12" class="pa-0 ma-0 d-flex flex-column align-left justify-center">
        <div>
          <v-icon color="#9c9c9c" style="font-size:18px;" class="pr-2 pb-1">mdi-map-marker</v-icon>
          <span style="font-size:17px;color:#9c9c9c;">{{ receiverLocation.name }}</span>
        </div>
      </v-col>
    </v-row>

    <!------------------------------------------>
    <!-- Provider order sync validation/guard -->
    <!------------------------------------------>
    <delivery-order-provider-sync
        v-if="isProviderLinked && isStepActive"
        class="mt-4"
        :order="order"
        @sync-state="handleProviderSyncState"
        @updated="handleProviderOrderUpdated" />

    <!-------------------->
    <!-- Driver message -->
    <!-------------------->
    <v-container class="driver-message-container mt-4 mb-3 pb-5" v-if="driverMessageItems.length > 0">
      <v-row>
        <v-col>
          <div class="d-flex align-center justify-space-between">
            <div class="d-flex align-center">
              <v-icon color="amber darken-2" class="pr-2">mdi-message-alert-outline</v-icon>
              <div class="driver-message-title">
                Message from Dispatcher
              </div>
            </div>
            <div class="d-flex align-center" style="color:#727272;" v-if="driverMessageItems[0].is_confirmed">
              <v-icon style="font-size:18px;color:#727272;" class="pr-2">mdi-eye-outline</v-icon>
              {{ formatDateTime(driverMessageItems[0].confirmed_at) }}
            </div>
            <div v-else-if="!hasCompleted">
              <v-btn
                  small
                  outlined
                  color="green darken-1"
                  @click="confirmDriverMessage(driverMessageItems[0])">
                Confirm Seen
              </v-btn>
            </div>
          </div>
        </v-col>
      </v-row>
      <v-row class="mt-1">
        <v-col>
          <div class="driver-message-text">
            {{ driverMessageItems[0].message }}
          </div>
        </v-col>
      </v-row>
    </v-container>

    <!------------------------------------------------------>
    <!-- Verify ordered items checklist & supporting docs -->
    <!------------------------------------------------------>
    <v-row class="pa-0 ma-0">
      <v-col sm="12" class="pa-0 ma-0 d-flex flex-column align-left justify-center">
        <div class="mt-4 content-title">
          Have receiving agent verify the order.
        </div>
        <div class="mt-5 d-flex align-center">
          <span style="font-size:18px;color:#9c9c9c;" class="mr-2">Order</span>
          <span style="font-size:25px" class="mr-3">{{ order.thirdparty_order_number }}</span>
        </div>
      </v-col>
    </v-row>
    <v-row class="pa-0 ma-0 mt-5 verify-order-container">
      <v-col cols="12" xs="12" sm="6" md="6" lg="6" class="py-2 d-flex align-center justify-center">
        <!-- Order checklist modal -->
        <div class="d-flex align-center">
          <v-dialog scrollable fullscreen persistent v-model="isViewOrderOpen">
            <!-- Activator button -->
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                  text
                  color="primary"
                  :disabled="isProviderOrderBlocked"
                  v-bind="attrs"
                  v-on="on">
                <v-icon class="mr-2" style="font-size:26px;" color="primary">mdi-playlist-check</v-icon>
                <span>View item checklist</span>
              </v-btn>
            </template>
            <!-- Dialog content -->
            <v-card class="dialog-content">
              <v-card-title style="border-bottom:1px solid #3c3c3c;">
                <v-row>
                  <v-col cols="8" xs="8" sm="8" md="8" lg="8">
                    <div>Order {{ order.thirdparty_order_number }}</div>
                    <div class="sub-title">Item Checklist</div>
                  </v-col>
                  <v-col cols="4" xs="4" sm="4" md="4" lg="4" class="d-flex align-center justify-end">
                    <v-btn large text icon color="primary" class="mb-2" @click="isViewOrderOpen = false">
                      <v-icon large>mdi-close</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-title>
              <v-card-text>
                <delivery-order-checklist
                    :order="order"
                    :checkOffMode="true"
                    v-on:closed="isViewOrderOpen = false" />
              </v-card-text>
            </v-card>
          </v-dialog>
        </div>
      </v-col>
      <v-col cols="12" xs="12" sm="6" md="6" lg="6" class="py-2 d-flex align-center justify-center">
        <!-- Supporting docs viewer modal -->
        <div class="d-flex align-center">
          <v-dialog scrollable fullscreen persistent v-model="isViewSupportingDocsOpen">
            <!-- Activator button -->
            <template v-slot:activator="{ on, attrs }">
              <v-btn text color="primary" v-bind="attrs" v-on="on">
                <v-icon class="mr-3" style="font-size:16px;" color="primary">mdi-file-multiple</v-icon>
                <span>View supporting docs</span>
              </v-btn>
            </template>
            <!-- Dialog content -->
            <v-card class="dialog-content">
              <v-card-title style="border-bottom:1px solid #3c3c3c;">
                <v-row>
                  <v-col cols="8" xs="8" sm="8" md="8" lg="8">
                    <div>Order {{ order.thirdparty_order_number }}</div>
                    <div class="sub-title">Supporting Docs</div>
                  </v-col>
                  <v-col cols="4" xs="4" sm="4" md="4" lg="4" class="d-flex align-center justify-end">
                    <v-btn large text icon color="primary" class="mb-2" @click="isViewSupportingDocsOpen = false">
                      <v-icon large>mdi-close</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-title>
              <v-card-text>
                <delivery-order-supporting-docs-viewer
                    class="mt-3"
                    :order="order"
                    v-on:closed="isViewSupportingDocsOpen = false" />
              </v-card-text>
            </v-card>
          </v-dialog>
        </div>
      </v-col>
    </v-row>

    <!------------------------------>
    <!-- Order rejection handling -->
    <!------------------------------>
    <v-row class="pa-0 ma-0 mt-2">
      <v-col sm="12" class="pa-0 ma-0 d-flex flex-column align-left justify-center">
        <div class="mt-2">
          <!-- Rejected alert -->
          <template v-if="isOrderRejected">
            <v-alert type="error" outlined class="mt-4">
              <div style="text-transform:uppercase !important">Order rejected</div>
              <div class="mt-2" v-if="order.receiver_notes">{{ order.receiver_notes }}</div>
            </v-alert>
            <v-btn
                v-if="!hasCompleted"
                plain
                color="primary"
                class="mb-2"
                @click="undoOrderRejected">
              Undo Rejection
            </v-btn>
          </template>
          <!-- Order rejection modal -->
          <v-dialog persistent width="450" v-model="isOrderRejectionFormOpen" v-else-if="!hasCompleted">
            <!-- Activator button -->
            <template v-slot:activator="{ on, attrs }">
              <v-btn plain color="amber darken-3" v-bind="attrs" v-on="on" :disabled="hasCompleted !== null">
                <v-icon style="font-size:20px;margin-right:6px;">mdi-alert-octagon</v-icon>
                <span>Problems with this order?</span>
              </v-btn>
            </template>
            <!-- Dialog content -->
            <v-card>
              <v-card-title class="mb-6">Reject Order {{ order.thirdparty_order_number }}</v-card-title>
              <v-card-text>
                <delivery-order-rejection-form
                    :order="order"
                    v-on:canceled="isOrderRejectionFormOpen = false"
                    v-on:rejected="setOrderRejected" />
              </v-card-text>
            </v-card>
          </v-dialog>
        </div>
      </v-col>
    </v-row>

    <!--------------------------------->
    <!-- Show saved FA card of agent -->
    <!--------------------------------->
    <template v-if="isAgentCardSaved && !changeAgentCard && !isOrderRejected">
      <v-row class="pa-0 ma-0 mt-9">
        <v-col sm="12" class="pa-0 ma-0 d-flex flex-column align-left justify-center">
          <div class="image-container">
            <iframe
                v-if="savedFaCardIsPdf && isIOS"
                class="saved-pdf-card"
                :src="savedFaCard"
                title="Receiving agent FA card PDF" />
            <embed
                v-else-if="savedFaCardIsPdf"
                class="saved-pdf-card"
                :src="savedFaCard"
                type="application/pdf" />
            <img v-else id="img" style="max-width:50%;max-height:50%;" :src="savedFaCard" />
          </div>
          <div class="mt-2" align="center">
            <span style="color:#4caf50;">AGENT CARD UPLOADED</span>
            <v-icon color="green" class="ml-2 mr-2">mdi-check-circle</v-icon>
          </div>
          <div class="mt-4" align="center" v-if="!hasCompleted">
            <v-btn
                text
                color="primary"
                :disabled="isProviderOrderBlocked"
                @click="clearFaCardInfo">
              Change FA card
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </template>

    <!--------------------------------------->
    <!-- Agent card uploader and extractor -->
    <!--------------------------------------->
    <!-- Otherwise, require FA card upload of agent -->
    <template v-else-if="!isOrderRejected">
      <v-row class="pa-0 ma-0 mt-9 mb-4">
        <v-col sm="12" class="pa-0 ma-0 d-flex flex-column align-left justify-center">
          <div class="content-title">
            Take a photo or upload a PDF of the receiving agent's FA card.
          </div>
        </v-col>
      </v-row>
      <v-row class="pa-0 ma-0 mt-4">
        <v-col sm="12" align="center" class="pa-0 ma-0 d-flex flex-column align-left justify-center">
          <!-- FA card upload button -->
          <v-btn
              v-if="isFileUploaderReady"
              text
              color="white"
              height="80"
              class="btn-take-photo"
              :disabled="isProviderOrderBlocked"
              @click="openCamera">
            <v-icon class="icon-take-photo">mdi-camera</v-icon>
          </v-btn>
          <!-- Message when FA card extractor is being initialized -->
          <!-- <div v-else>
            <v-icon large color="amber accent-4">mdi-progress-alert</v-icon>
            <div style="color:#FFAB00;" class="mt-2">FA card extractor is initializing. Please wait...</div>
          </div> -->
          <!-- Message when FA card extractor was aborted (could not be loaded) -->
          <!-- <div v-if="faCardExtractorError">
            <div>
              <v-icon color="amber accent-4">mdi-alert-outline</v-icon>
              <span style="color:#FFAB00;">The FA card extraction utility could not be loaded.</span>
            </div>
            <div>{{ faCardExtractorError }}</div>
          </div> -->
          <delivery-file-uploader
              ref="fa-card-uploader"
              class="fa-card-upload-ui"
              :showCancel="true"
              :orderId="order.id"
              assetKey="agent_fa_license"
              v-on:aborted="handleAbortedFaCardExtractor"
              v-on:initialized="isFileUploaderReady = true"
              v-on:saved="handleSavedAgentFaCard" />

          <!-- FA card upload error -->
          <div class="mt-3 mb-2 align-center" v-if="faCardUploadError">
            <div class="d-flex align-center">
              <v-icon color="amber accent-4" class="mr-3">mdi-alert-outline</v-icon>
              <div style="text-align:left;color:#FFAB00;">{{ faCardUploadError }}</div>
            </div>
            <v-btn small outlined color="grey" width="80" class="mt-2" @click="clearFaCardUploadError">Retry</v-btn>
          </div>
        </v-col>
      </v-row>
    </template>

    <!-- (FA Card Scanner dialog) -->
    <v-dialog fullscreen persistent v-model="isFaCardScannerOpen">
      <fa-card-scanner
         v-if="isFaCardScannerOpen"
         v-on:captured="handleCapturedFaCard"
         v-on:close="isFaCardScannerOpen = false" />
    </v-dialog>

    <!------------------------------->
    <!-- Name and license of agent -->
    <!------------------------------->
    <v-row class="pa-0 ma-0 mt-6">
      <v-col sm="12" class="pa-0 ma-0 d-flex flex-column align-left justify-center">
        <div class="content-title">
          <span v-if="isOrderRejected">Provide the name of the agent.</span>
          <span v-else>Provide the name and FA license number for the agent.</span>
        </div>
        <div class="mt-4 input-label-text">
          Agent name
        </div>
        <v-text-field
            outlined
            hide-details="auto"
            class="mt-2"
            :disabled="hasCompleted !== null || isProviderOrderBlocked"
            v-model.trim="order.receiving_agent_name" />
        <template v-if="!isOrderRejected">
          <div class="mt-3 input-label-text">
            Agent FA license #
          </div>
          <v-text-field
              maxlength="20"
              outlined
              hide-details="auto"
              class="mt-2"
              :rules="licenseRules"
              :disabled="hasCompleted !== null || isProviderOrderBlocked"
              v-model.trim="order.receiving_agent_fa_license_number" />
        </template>
      </v-col>
    </v-row>
    <!-- Notification text per automatically extracted info -->
    <template v-if="enableFaCardScanner && extractedAgentInfoMessage">
      <transition name="fade" appear>
        <v-row class="pa-0 ma-0 mt-4">
          <v-col cols="12" class="pa-0 ma-0 d-flex flex-column align-left justify-center">
            <div>
              <v-icon color="#FFB300" style="font-size:18px;" class="pr-2 pb-1">mdi-alert-circle-outline</v-icon>
              <span style="font-size:17px;color:#FFB300;">{{ extractedAgentInfoMessage }}</span>
            </div>
          </v-col>
        </v-row>
      </transition>
    </template>

    <!--------------------------------------------------------->
    <!-- Copy previous receiving agent info (Agent Selector) -->
    <!--------------------------------------------------------->
    <template v-if="previousOrdersForThisReceiver && previousOrdersForThisReceiver.length > 0">
      <v-row class="pa-0 ma-0 mt-5">
        <v-col sm="12" class="pa-0 ma-0 d-flex flex-column align-left justify-center">
          <!-- Agent selection modal -->
          <v-dialog fullscreen persistent v-model="isAgentSelectorOpen">
            <!-- Activator button -->
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                  large
                  outlined
                  class="mt-3"
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                  :disabled="hasCompleted !== null || isProviderOrderBlocked">
                <v-icon class="icon-open-in-new">mdi-content-copy</v-icon>
                <span>Copy previous agent info</span>
              </v-btn>
            </template>
            <!-- Dialog content -->
            <v-card>
              <v-card-title>Agents from {{ receiverLocation.name }}</v-card-title>
              <v-card-text>
                <agent-selector
                    :agentData="previousOrdersForThisReceiver"
                    v-on:canceled="isAgentSelectorOpen = false"
                    v-on:selected="copyAgentInfo" />
              </v-card-text>
            </v-card>
          </v-dialog>
        </v-col>
      </v-row>
    </template>

    <!------------------>
    <!-- Payment info -->
    <!------------------>
    <v-row class="pa-0 ma-0 mt-12">
      <v-col sm="12" class="pa-0 ma-0 d-flex flex-column align-left justify-center">
        <div class="content-title">
          Was a payment provided at delivery?
        </div>
        <div class="pl-2 input-label-text">
          <v-radio-group
              row
              :disabled="hasCompleted !== null || isProviderOrderBlocked"
              v-model="deliveryInfo.paid_on_delivery">
            <v-radio label="Yes" :value="true" />
            <v-radio label="No" :value="false" />
          </v-radio-group>
        </div>
      </v-col>
    </v-row>
    <v-row class="pa-0 ma-0">
      <v-col cols="12" class="pa-0 ma-0 d-flex flex-column align-left justify-center">
        <div class="pl-6" style="border:1px solid #a1451f;border-radius:4px;" v-if="deliveryInfo.paid_on_delivery">
          <div style="font-size:16px;color:#9c9c9c;" class="pt-2 pb-4">
            Select payment type:
          </div>
          <v-radio-group
              class="ma-0"
              :disabled="hasCompleted !== null || isProviderOrderBlocked"
              v-model="deliveryInfo.payment_type">
            <v-radio label="Cash" value="cash" />
            <v-radio label="Check" value="check" />
            <v-radio label="ACH" value="ach" />
          </v-radio-group>
        </div>
      </v-col>
    </v-row>

    <!------------------------>
    <!-- Signature of agent -->
    <!------------------------>
    <v-row class="pa-0 ma-0 mt-8">
      <v-col sm="12" class="pa-0 ma-0 d-flex flex-column align-left justify-center">
        <div class="content-title">
          Have receiving agent sign.
        </div>
        <!-- Signature Modal -->
        <v-dialog fullscreen persistent v-model="isAgentSignatureOpen">
          <!-- (activator button) -->
          <template v-slot:activator="{ on, attrs }">
            <v-btn
                text
                width="240"
                class="mt-2"
                color="primary"
                v-bind="attrs"
                v-on="on"
                :disabled="!isAgentCardSaved || !isAgentInfoSaved || hasCompleted !== null || isProviderOrderBlocked">
              <v-icon class="icon-open-in-new">mdi-open-in-new</v-icon>
              <span>Agent E-Signature</span>
            </v-btn>
          </template>
          <!-- (signature UI) -->
          <delivery-signature
              :orderId="order.id"
              :isRejected="isOrderRejected"
              :showReceiveConfirm="true"
              assetKey="agent_signature"
              v-on:canceled="isAgentSignatureOpen = false"
              v-on:saved="handleSavedAgentSignature">
            <template v-slot:title>
              <div class="agent-signature-heading">
                <div class="agent-signature-heading-label">Agent Signature</div>
                <div class="agent-signature-heading-details">
                  <span class="agent-signature-heading-name">{{ order.receiving_agent_name }}</span>
                  <span
                      class="agent-signature-heading-license"
                      v-if="order.receiving_agent_fa_license_number">
                    FA# {{ order.receiving_agent_fa_license_number }}
                  </span>
                </div>
              </div>
            </template>
          </delivery-signature>
        </v-dialog>

        <!-- Signature upload error -->
        <div class="mt-4 mb-2 align-center" v-if="signatureUploadError">
          <div class="d-flex align-center">
            <v-icon color="amber accent-4" class="mr-3">mdi-alert-outline</v-icon>
            <div style="text-align:left;color:#FFAB00;">{{ signatureUploadError }}</div>
          </div>
          <div class="d-flex justify-center mt-3">
            <v-btn small outlined color="grey" width="80" @click="clearSignatureUploadError">Retry</v-btn>
          </div>
        </div>
      </v-col>
    </v-row>
    <!-- (completed agent signature image) -->
    <v-row class="pa-0 ma-0 mt-4" v-if="savedSignature">
      <v-col sm="12" class="pa-0 ma-0 d-flex flex-column align-center justify-center">
        <div class="signature-area">
          <img
              class="saved-agent-signature"
              :src="savedSignature"
              alt="Agent signature" />
        </div>
        <div class="mt-2">
          <span style="color:#4caf50;">AGENT SIGNED</span>
          <v-icon color="green" class="pl-2">mdi-check-circle</v-icon>
        </div>
      </v-col>
    </v-row>

    <!------------------------------>
    <!-- E-sign delivery (driver) -->
    <!------------------------------>
    <v-row class="pa-0 ma-0 mt-10 mb-12">
      <v-col cols="12" class="pa-0 ma-0 d-flex flex-column align-left justify-center" v-if="!hasDriverSigned">
        <div class="pb-3 disclaimer-text">
          <span>{{ driverTerms }}</span>
        </div>
        <v-btn
            color="light-green darken-2"
            width="260"
            :disabled="!hasAgentSigned || isProviderOrderBlocked"
            @click="recordDriverSignature">
          <v-progress-circular indeterminate :size="20" color="white" v-if="isRecordingSignature" />
          <template v-else>
            <v-icon color="white" class="mr-1">mdi-draw</v-icon>
            <span class="mr-3">Click to e-sign</span>
          </template>
        </v-btn>
      </v-col>
      <v-col cols="12" class="pa-0 ma-0 d-flex flex-column align-center justify-center" v-else>
        <!-- (completed driver signature image) -->
        <div class="mt-4 signature-area" v-if="driverSignature">
          <div>
            <img style="max-width:50%;max-height:50%;" :src="driverSignature" />
          </div>
        </div>
        <div class="mt-2">
          <span style="color:#4caf50;">DRIVER SIGNED</span>
          <v-icon color="green" class="pl-2">mdi-check-circle</v-icon>
        </div>
      </v-col>
    </v-row>

    <!--------------------------->
    <!-- Optional driver notes -->
    <!--------------------------->
    <v-row class="pa-0 ma-0 driver-notes-area">
      <v-col sm="12" class="pa-0 ma-0 mt-2">
        <div class="content-title">
          <span v-if="isOrderRejected">Notate route changes for this order.</span>
          <span v-else>Driver notes (optional)</span>
        </div>
        <v-textarea
            v-if="hasCompleted !== null"
            outlined
            auto-grow
            class="mt-4"
            rows="2"
            :disabled="true"
            :value="savedDriverNotes" />
        <v-textarea
            v-else
            counter
            outlined
            auto-grow
            class="mt-4"
            rows="2"
            :disabled="hasCompleted !== null || isProviderOrderBlocked"
            v-model.trim="driverNotes" />
      </v-col>
    </v-row>

    <!------------------->
    <!-- Complete step -->
    <!------------------->
    <v-row class="mt-10 complete-step">
      <v-col sm="12" class="pa-0 ma-0 d-flex flex-column align-left justify-center">
        <v-btn color="primary" v-if="isDeliverProcessing">
          <v-progress-circular indeterminate :size="20" color="white" />
        </v-btn>
        <v-btn
            large
            color="primary"
            :disabled="true"
            v-else-if="hasCompleted && isOrderRejected">
          Rejected at {{ formatDateTime(order.rejected_at) }}
        </v-btn>
        <v-btn
            large
            color="primary"
            :disabled="true"
            v-else-if="hasCompleted">
          Delivered at {{ formatDateTime(hasCompleted) }}
        </v-btn>
        <double-click-confirm-button
            v-else
            buttonText="Complete Order"
            confirmTitle="Order has been delivered."
            :confirmMessageText="(isTestRoute) ? '' : 'This will notify all relevant parties.'"
            :disabled="!isReadyToComplete || !isConfirmDeliverSignature || isProviderOrderBlocked"
            @confirmed="completeStep" />
      </v-col>
    </v-row>
  </v-container>

</template>

<script>
/* eslint-disable no-unneeded-ternary */
import { mapGetters } from 'vuex'
import DeliveryOrderProviderSync from '@/components/deliveries/DeliveryOrderProviderSync'
import DoubleClickConfirmButton from '@/components/app/DoubleClickConfirmButton'
import DeliveryOrderRejectionForm from '@/components/deliveries/DeliveryOrderRejectionForm'
import DeliveryOrderSupportingDocsViewer from '@/components/deliveries/DeliveryOrderSupportingDocsViewer'
import DeliveryOrderChecklist from '@/components/deliveries/DeliveryOrderChecklist'
import DeliveryFileUploader from '@/components/deliveries/DeliveryFileUploader'
import DeliverySignature from '@/components/deliveries/DeliverySignature'
import AgentSelector from '@/components/agents/AgentSelector'
import FaCardScanner from '@/components/app/FaCardScanner'
import Ocr from '@/services/ocr'

export default {
  name: 'DeliveryStepDeliverOrder',

  components: {
    DeliveryOrderProviderSync,
    DoubleClickConfirmButton,
    DeliveryOrderRejectionForm,
    DeliveryOrderSupportingDocsViewer,
    DeliveryOrderChecklist,
    DeliveryFileUploader,
    DeliverySignature,
    AgentSelector,
    FaCardScanner
  },

  props: {
    route: {
      type: Object,
      required: true
    },
    order: {
      type: Object,
      required: true
    },
    stepNumber: {
      type: Number,
      required: true
    },
    isStepActive: {
      type: Boolean,
      default: true
    }
  },

  data () {
    return {
      // ------ Driver messages
      isMessageDataLoading: false,
      isConfirmingMessage: false,
      driverMessageData: null,
      // ------ FA card handling
      isFileUploaderReady: false,
      faCardExtractorError: null,
      faCardUploadError: null,
      extractedAgentInfoMessage: null,
      isFaCardScannerOpen: false,
      isBarcodeScannerOpen: false,
      savedFaCard: null,
      savedFaCardIsPdf: false,
      changeAgentCard: null,
      // ------ Signature handling
      isAgentSignatureOpen: false,
      signatureUploadError: null,
      signatureImageData: null,
      savedSignature: null,
      driverSignature: null,
      isRecordingSignature: false,
      // ------ General state mgmt
      isDeliverProcessing: false,
      isOrderRejectionFormOpen: false,
      isAgentSelectorOpen: false,
      isViewOrderOpen: false,
      isViewSupportingDocsOpen: false,
      isProviderOrderSyncBlocked: true,
      driverNotes: null,
      licenseRules: [
        v => /^$|^([0-9]|[a-z]|[A-Z]){20}$/.test(v) || 'License number must be exactly 20 alpha-numeric characters'
      ]
    }
  },

  computed: {
    ...mapGetters('app', ['enableFaCardScanner']),
    isIOS () {
      const userAgent = navigator.userAgent || ''
      const isIOSDevice = /iPad|iPhone|iPod/.test(userAgent)
      const isIPadViaDesktopMode = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1
      return isIOSDevice || isIPadViaDesktopMode
    },
    isTestRoute () {
      return this.route.is_test
    },
    isProviderLinked () {
      return Boolean(this.order.provider_alias && this.order.thirdparty_order_id)
    },
    isProviderOrderBlocked () {
      return this.isProviderLinked && this.isProviderOrderSyncBlocked
    },
    receiverLocation () {
      return this.order.receiver
    },
    previousOrdersForThisReceiver () {
      return this.route.orders.filter(order => order.receiver_id === this.receiverLocation.id && order.id !== this.order.id && order.receiver_signed_at)
    },
    deliveryInfo () {
      return this.order.delivery_info
    },
    driverMessageItems () {
      return (this.driverMessageData) ? this.driverMessageData.items : []
    },
    savedDriverNotes () {
      if (this.order.driver_notes && this.order.driver_notes.dropoff) {
        return this.order.driver_notes.dropoff // latest architecture
      } else if (this.route.driver_notes && this.route.driver_notes[this.stepNumber]) {
        return this.route.driver_notes[this.stepNumber] // old architecture
      } else {
        return ''
      }
    },
    driverTerms () {
      return (this.isOrderRejected)
        ? 'I have accepted the return of a rejected order.'
        : 'I have delivered the order to a licensed facility agent.'
    },
    // driverNotesLengthRule () {
    //   return [
    //     v => (!v || v.length <= 96) || 'Max 96 characters'
    //   ]
    // },
    isOrderRejected () {
      return (this.order.order_status === 'REJECTED_ON_DELIVERY')
        ? true
        : false
    },
    isAgentCardSaved () {
      if (this.isOrderRejected) {
        return true // skip requirement on rejection
      } else {
        return this.order.receiver_card_saved_at
      }
    },
    isAgentInfoSaved () {
      if (this.isOrderRejected) {
        return this.order.receiving_agent_name
      } else {
        return this.order.receiving_agent_name && this.order.receiving_agent_fa_license_number
      }
    },
    hasAgentSigned () {
      return (this.order.receiver_signed_at)
        ? true
        : false
    },
    isReadyToComplete () {
      return (this.isAgentCardSaved && this.isAgentInfoSaved && this.hasAgentSigned && this.hasDriverSigned)
        ? true
        : false
    },
    isConfirmDeliverSignature () {
      return (this.order.driver_deliver_signed_at)
        ? true
        : false
    },
    hasDriverSigned () {
      return (this.order.driver_deliver_signed_at)
        ? true
        : false
    },
    hasCompleted () {
      return this.order.completed_at
    }
  },

  async beforeMount () {
    // Load driver messages
    await this.loadDriverMessages()

    // Load agent FA card and signature, if already saved
    if (this.isAgentCardSaved) await this.loadSavedFaCard()
    if (this.hasAgentSigned) await this.loadSavedAgentSignature()

    // Load driver signature, if already saved
    if (this.hasDriverSigned) await this.loadDriverSignature()
  },

  async mounted () {
    // Load saved driver notes
    if (this.savedDriverNotes && this.savedDriverNotes.length > 0) this.driverNotes = this.savedDriverNotes

    // Enable FA scanner
    if (this.enableFaCardScanner) await Ocr.initialize()
  },

  unmounted () {
    Ocr.destroy()
  },

  methods: {
    // Display a date/time
    formatDateTime (date) {
      return (date) ? this.$root.utils.dateFormat(date, 'MMM D h:mma') : ''
    },

    formatAddress (data = {}) {
      return this.$root.utils.getAddressTextFromData(data)
    },

    openCamera () {
      if (this.isProviderOrderBlocked) return

      if (this.enableFaCardScanner) {
        // Trigger FA Card Scanner solution
        this.isFaCardScannerOpen = true
      } else {
        // Trigger Delivery File Uploader solution
        const uploader = this.$refs['fa-card-uploader']
        if (uploader) uploader.trigger()
      }
    },

    // Handling logic for the FA Card Scanner solution
    async handleCapturedFaCard ({ file, width, height, nameRect, licenseNumberRect }) {
      let agentInfoExtracted = false

      // Attempt to auto-fill from FA card scanner utility
      const name = await Ocr.extractSingleLine(file, nameRect)
      if (name.length > 0) {
        // Convert to title case
        const formattedName = this.$root.utils.toStartCase(name.toLowerCase())
        this.order.receiving_agent_name = formattedName
        agentInfoExtracted = true
      }

      const licenseNumber = await Ocr.extractSingleLine(file, licenseNumberRect, false)
      if (licenseNumber.length > 0) {
        // Correct inconsistencies with interpreted font
        const correctedLicenseNumber = licenseNumber.replace(/\)/g, 'J')
        this.order.receiving_agent_fa_license_number = correctedLicenseNumber
        agentInfoExtracted = true
      }

      // Flash message to user per extracted info
      if (agentInfoExtracted) {
        this.extractedAgentInfoMessage = 'Please validate agent info is accurate'
        setTimeout(() => {
          this.extractedAgentInfoMessage = null
        }, 6000) // 6 sec
      }

      const uploader = this.$refs['fa-card-uploader']
      await uploader.setFile(file)
    },

    // Load driver messages for the order
    async loadDriverMessages () {
      this.isMessageDataLoading = true

      try {
        this.driverMessageData = await this.$store.dispatch('sh/dataApi', {
          method: 'get',
          uri: `order/${this.order.id}/driver-messages`
        })
      } catch (err) {
        // this.dataLoadingError = err.message || err.text || err
        console.error(err)
      } finally {
        this.isMessageDataLoading = false
      }
    },

    handleProviderSyncState (isBlocked) {
      this.isProviderOrderSyncBlocked = isBlocked
    },

    handleProviderOrderUpdated (updatedOrder) {
      if (!updatedOrder) return

      Object.assign(this.order, updatedOrder)
      const syncOptions = {
        resource: 'activeDelivery',
        nested: 'orders',
        data: this.order
      }
      this.$store.dispatch('sh/syncData', syncOptions)
      this.$store.dispatch('sh/syncItemEditing', syncOptions)
    },

    // Confirm driver message was seen
    async confirmDriverMessage (messageItem) {
      if (!messageItem) return false
      this.isConfirmingMessage = true

      try {
        await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: `order-driver-message/${messageItem.id}/confirm`
        })

        // Reload driver messages
        this.loadDriverMessages()
      } catch (err) {
        // this.dataLoadingError = err.message || err.text || err
        console.error(err)
      } finally {
        this.isConfirmingMessage = false
      }
    },

    async loadDriverSignature () {
      const signature = await this.$root.fetchUserAsset('signature', this.route.driver_user_id)
      this.driverSignature = signature
    },

    async loadSavedFaCard (uploadedFile = null) {
      const agentFaCardImage = await this.$root.fetchOrderAsset(this.order.id, 'agent_fa_license')
      this.savedFaCard = agentFaCardImage

      if (uploadedFile) {
        this.savedFaCardIsPdf = uploadedFile.type === 'application/pdf' || /\.pdf$/i.test(uploadedFile.name || '')
        return
      }

      try {
        const response = await fetch(agentFaCardImage)
        const contentType = (response.headers.get('content-type') || '').toLowerCase()
        this.savedFaCardIsPdf = contentType.includes('application/pdf')
      } catch (err) {
        this.savedFaCardIsPdf = false
        console.error('Error determining agent FA card file type =>', err)
      }
    },

    async loadSavedAgentSignature () {
      const agentSignatureImage = await this.$root.fetchOrderAsset(this.order.id, 'agent_signature')
      this.savedSignature = agentSignatureImage
    },

    clearFaCardInfo () {
      this.changeAgentCard = true
      this.order.receiving_agent_name = null
      this.order.receiving_agent_fa_license_number = null
    },

    clearFaCardUploadError () {
      this.faCardUploadError = null
    },

    clearSignatureUploadError () {
      this.signatureUploadError = null
    },

    copyAgentInfo (data) {
      this.order.receiving_agent_name = data.receiving_agent_name
      this.order.receiving_agent_fa_license_number = data.receiving_agent_fa_license_number
      this.isAgentSelectorOpen = false
    },

    handleAbortedFaCardExtractor (err) {
      this.isFileUploaderReady = true
      this.faCardExtractorError = (err && err.message) ? err.message : err
    },

    // Handling logic for the Delivery File Uploader solution
    async handleSavedAgentFaCard (data) {
      this.faCardUploadError = null

      // Insert extracted info (if FA card scanner utility is not being used)
      if (!this.enableFaCardScanner && !this.$root.utils.isEmpty(data.extracted_data)) {
        this.copyAgentInfo(data.extracted_data)
      }

      // Update order with saved card timestamp
      if (data.uploaded === true) {
        this.changeAgentCard = false
        this.order.receiver_card_saved_at = new Date().toISOString()
        try {
          await this.$store.dispatch('sh/updateData', {
            resource: 'order',
            data: this.order,
            noFeedback: true
          })

          // Load saved card to display
          this.loadSavedFaCard(data.file)
        } catch (err) {
          console.error('Error saving agent FA card timestamp =>', err)
        }
      } else {
        this.faCardUploadError = data.error
      }
    },

    async handleSavedAgentSignature (data) {
      this.signatureUploadError = null

      // Update order with saved signature timestamp and terms
      if (data.uploaded === true) {
        this.order.receiver_signed_at = new Date().toISOString()
        this.order.delivery_info.receiver_terms = data.terms
        try {
          await this.$store.dispatch('sh/updateData', {
            resource: 'order',
            data: this.order,
            noFeedback: true
          })

          // Load saved signature to display
          this.loadSavedAgentSignature()
        } catch (err) {
          console.error('Error saving agent signature timestamp =>', err)
        }
      } else {
        this.signatureUploadError = data.error
      }

      this.isAgentSignatureOpen = false
    },

    // Update order with saved signature timestamp
    async handleConfirmDeliverSignature () {
      this.order.driver_deliver_signed_at = new Date().toISOString()
      try {
        await this.$store.dispatch('sh/updateData', {
          resource: 'order',
          data: this.order,
          noFeedback: true
        })
      } catch (err) {
        console.log(`Error saving driver deliver signature timestamp for order ${this.order.id} =>`, err)
      }
    },

    // Update order with saved signature timestamp
    async recordDriverSignature () {
      this.isRecordingSignature = true

      // Load signature to display
      await this.loadDriverSignature()

      try {
        const data = {
          orderId: this.order.id
        }
        await this.$store.dispatch('sh/recordDriverSignatureAtDelivery', data)
      } catch (err) {
        console.log('Error recording driver signature =>', err)
      }

      this.isRecordingSignature = false
    },

    async setOrderRejected (data = {}) {
      this.order.receiver_notes = data.notes
      this.order.order_status = 'REJECTED_ON_DELIVERY' // manually set status to save state

      this.isOrderRejectionFormOpen = false

      try {
        await this.$store.dispatch('sh/updateData', {
          resource: 'order',
          data: this.order,
          noFeedback: true
        })
      } catch (err) {
        console.log(`Error updating order on rejection ${this.order.id} =>`, err)
      }
    },

    async undoOrderRejected (data = {}) {
      this.order.receiver_notes = ''
      this.order.order_status = 'AT_RECEIVER' // manually set status to save state

      try {
        await this.$store.dispatch('sh/updateData', {
          resource: 'order',
          data: this.order,
          noFeedback: true
        })
      } catch (err) {
        console.log(`Error updating order on undo rejection ${this.order.id} =>`, err)
      }
    },

    // Set order: delivered, and complete step
    async completeStep () {
      this.isDeliverProcessing = true

      // Include driver terms, clean up final payment info
      this.order.delivery_info.driver_terms = this.driverTerms
      if (!this.order.delivery_info.paid_on_delivery && this.order.delivery_info.payment_type) delete this.order.delivery_info.payment_type

      try {
        const data = {
          orderId: this.order.id,
          agentName: this.order.receiving_agent_name,
          agentLicenseNumber: this.order.receiving_agent_fa_license_number,
          agentLicenseExpiration: null,
          deliveryInfo: this.order.delivery_info,
          driverNotes: this.driverNotes
        }

        // Handle order completion
        if (this.isOrderRejected) {
          // Complete rejected order
          await this.$store.dispatch('sh/rejectOrder', data)
        } else {
          // Handle delivered order
          await this.$store.dispatch('sh/setOrderDelivered', data)
        }

        // Complete step
        this.$emit('completed')
      } catch (err) {
        if (this.isOrderRejected) console.log('Error setting order: rejected =>', err)
        else console.log('Error setting order: delivered =>', err)
      }

      this.isDeliverProcessing = false
    }
  }
}
</script>

<style>
  .btn-take-photo {
    border: 1px dashed #ffffff;
  }

  .icon-take-photo {
    font-size: 36px !important;
  }

  .fa-card-upload-ui {
    margin-top: -2px !important;
  }

  .verify-order-container {
    border: 1px solid #727272;
    border-radius: 6px;
  }

  /* Driver Messages */
  .delivery-step-obtain-receiver-info .driver-message-container {
    border: 1px solid #FF9F00;
    border-radius: 4px;
    background-color: #252525;
  }
  .delivery-step-obtain-receiver-info .driver-message-title {
    font-size: 1.04rem;
    color: #9c9c9c;
    text-transform: uppercase !important;
    letter-spacing: 0.7px;
    font-weight: 400;
  }
  .delivery-step-obtain-receiver-info .driver-message-text {
    color: #ffffff;
  }

  .delivery-step-obtain-receiver-info .image-container {
    border: 1px solid #525252;
    border-radius: 8px;
    padding: 4px 0 0 0;
    min-height: 150px;
    text-align: center;
  }

  .delivery-step-obtain-receiver-info .image-container img {
    max-height: 150px !important;
  }

  .delivery-step-obtain-receiver-info .saved-pdf-card {
    border: 0;
    height: 400px;
    width: 100%;
  }

  .delivery-step-obtain-receiver-info .signature-area {
    width: 100%;
    overflow: hidden;
    background-color: #ffffff;
    text-align: center;
    border-radius: 4px;
  }

  .delivery-step-obtain-receiver-info .saved-agent-signature {
    display: block;
    width: 100%;
    max-width: 100%;
    height: auto;
    object-fit: contain;
  }

  .agent-signature-heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    column-gap: 24px;
    width: 100%;
    line-height: 1.25;
  }

  .agent-signature-heading-label {
    flex: 0 0 auto;
    text-align: left;
  }

  .agent-signature-heading-details {
    display: flex;
    flex: 1 1 auto;
    min-width: 0;
    flex-direction: column;
    align-items: flex-end;
    text-align: right;
  }

  .agent-signature-heading-name {
    font-size: 1.3rem;
    font-weight: 600;
    overflow-wrap: anywhere;
  }

  .agent-signature-heading-license {
    font-size: 1.05rem;
    font-weight: 500;
    white-space: nowrap;
  }

  .driver-notes-area .v-text-field__slot textarea {
    margin-bottom: 12px;
  }

  .dialog-content .sub-title {
    font-size: 0.8em;
    color: #727272;
    text-transform: uppercase !important;
    letter-spacing: 1.7px;
    margin-top: -2px;
  }
</style>
